import React, { useMemo, useState } from 'react';
import { FiChevronDown, FiPrinter } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import usePermission from '@/hooks/usePermission';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import { FiCheckCircle } from 'react-icons/fi';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import { confirm } from '@/lib/confirm';
import { ExamsResolutionAnswerKey } from '@/pages/exams/exams-resolution/exams-resolution-answer-key';
import { t } from 'i18next';
import { Badge, BadgeTruncate } from '@/components/ui/badge';
import { useSession } from '@/hooks/useSession';
import IconSimulated from '@/components/custom/icons/IconSimulated';
import { Sheet, SheetBody, SheetContent } from '@/components/ui/sheet';
import { Card, CardBody, CardBodyContent, CardBodyHeader, CardBodyHeaderContent, CardContent, CardFooter, CardHeader, CardIcon, CardIconImg, CardIconVerified, CardKpi, CardKpiGrid, CardKpiTitle, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

export function MockExamsCard({ mockQuestionBook, setFilteredMockQuestionBooks }) {
  const [showModal, setShowModal] = useState(false);
  const client = useClient();
  const navigate = useNavigate();
  const sessionData = useSession();
  const currentUser = sessionData?.session?.user;
  const authorityLevel = useAuthorityLevel();
  const isStudent = authorityLevel === 'student';
  const canEditPermission = usePermission('Editar simulados', 'Provas e simulados');
  const canDeletePermission = usePermission('Excluir simulados', 'Provas e simulados');
  const canManageMockExam = usePermission('Gerenciar simulados', 'Provas e simulados');

  const mockQuestionBookForm = useFormik({
    initialValues: {
      id: mockQuestionBook?.id,
      published: mockQuestionBook?.published
    }
  });

  const updateMockQuestionBookPublicity = async values => {
    const { error } = await client.mutate(['question_books', mockQuestionBook.id], {
      published: !values.published
    });
    if (error) {
      toast.error(t('toast.errorPublishTask'));
    } else {
      if (!values.published === true) {
        toast.success(t('toast.successPublish'));
      } else {
        toast.success(t('toast.successUnpublish'));
      }
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t('warning.warningDeleteSimulation'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteMockQuestionBookCard();
    }
  };

  const deleteMockQuestionBookCard = async () => {
    const { error } = await client.delete(['question_books', mockQuestionBook?.id]);
    if (error) {
      toast.error(t('toast.errorDeleteSimulation'));
    } else {
      toast.success(t('toast.successSimulationDeleted'));
      setFilteredMockQuestionBooks(prevState => prevState.filter(item => item.id !== mockQuestionBook?.id));
    }
  };

  const isAcceptingAnswers = mockQuestionBook.status === 'accepting_answers';
  const isExamDone = mockQuestionBook.status === 'approved' || mockQuestionBook.status === 'disapproved';
  const isExamNotRealized = !mockQuestionBook.status;
  const isInAnalisys = mockQuestionBook.status === 'review';

  const actualDate = moment();
  const deadline = moment(mockQuestionBook?.['finished-at']);
  const isAfter = actualDate.isAfter(deadline);
  const isRealizedInAdmin = isAfter && !isStudent;

  const shouldRenderOptions = useMemo(() => {
    if (!currentUser) return false;

    const isOwner = mockQuestionBook?.creator?.id === currentUser.id;
    const isAdmin = currentUser.profileNames?.includes('Administrador');

    if (mockQuestionBook?.['authored-by-user']) {
      return isOwner || isAdmin;
    }

    return canEditPermission || canDeletePermission || canManageMockExam;
  }, [mockQuestionBook, currentUser, canEditPermission, canDeletePermission, canManageMockExam]);

  const statusBadge = useMemo(() => {
    if (isExamNotRealized && isStudent) {
      return { variant: 'danger', text: t('exams.statusUnrealized') };
    }
    if ((isExamDone && isStudent) || isRealizedInAdmin) {
      return { variant: 'success', text: t('exams.statusDone') };
    }
    if (isAcceptingAnswers && isStudent) {
      return { variant: 'warning', text: 'Aceitando respostas' };
    }
    if (isInAnalisys && isStudent) {
      return { variant: 'warning', text: t('exams.statusInAnalysis') };
    }
    return null;
  }, [isExamNotRealized, isExamDone, isStudent, isRealizedInAdmin, isAcceptingAnswers, isInAnalisys]);

  return (
    <Card hover>
      {!isStudent && (
        <CardHeader className="min-h-8 flex-row">
          <div className="form__check form__switch !mb-0 grow">
            {(mockQuestionBook?.['authored-by-user'] === false || currentUser.id === mockQuestionBook.creator.id || currentUser.profileNames.includes('Administrador')) && (
              <>
                <input
                  className="form__check-input"
                  id={`publish-${mockQuestionBook?.id}`}
                  name={`publish-${mockQuestionBook?.id}`}
                  type="checkbox"
                  checked={mockQuestionBookForm.values.published}
                  onClick={() => {
                    mockQuestionBookForm.setFieldValue('published', !mockQuestionBookForm.values.published);
                    updateMockQuestionBookPublicity(mockQuestionBookForm.values);
                  }}
                />
                <label htmlFor="">{t('exams.publish')}</label>
              </>
            )}
          </div>

          {mockQuestionBook?.['question-count'] > 0 && mockQuestionBook?.['question-count'] <= 100 && (
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              title={t('button.printExam')}
              onClick={() => navigate(`/avaliacoes/simulados/impressao/${mockQuestionBook?.id}`)}
            >
              <FiPrinter />
            </Button>
          )}
        </CardHeader>
      )}

      <CardContent>
        <CardIcon>
          {mockQuestionBook?.['official-content'] && <CardIconVerified />}
          <CardIconImg>
            <IconSimulated />
          </CardIconImg>
        </CardIcon>

        <CardBody>
          <CardBodyHeader>
            <CardBodyHeaderContent>
              <CardTitle title={mockQuestionBook?.title}>{mockQuestionBook?.title}</CardTitle>
            </CardBodyHeaderContent>

            {statusBadge && (
              <Badge
                variant={statusBadge.variant}
                size="xs"
              >
                {statusBadge.text}
              </Badge>
            )}
          </CardBodyHeader>

          <CardBodyContent>
            <BadgeTruncate
              oneLine
              items={mockQuestionBook?.taxonomies}
              variant="primary-light"
              size="sm"
              initialCount={1}
            />

            <CardKpiGrid>
              <CardKpi
                title="Questões"
                aria-label="Questões"
              >
                <FiCheckCircle />
                {mockQuestionBook?.['question-count']}
              </CardKpi>
              <CardKpi>
                <CardKpiTitle>{t('exams.labelPublishedAt')}</CardKpiTitle>
                {moment(mockQuestionBook?.['published-at']).format('DD/MM/YYYY')}
              </CardKpi>
            </CardKpiGrid>
          </CardBodyContent>
        </CardBody>
      </CardContent>

      <CardFooter>
        <ButtonGroup className="w-full">
          {isStudent ? (
            <>
              {isAcceptingAnswers && (
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  asChild
                >
                  <Link to={`/simulados/sobre/${mockQuestionBook?.id}`}>{t('button.access')}</Link>
                </Button>
              )}

              {isExamDone && (
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  onClick={() => setShowModal(true)}
                  disabled={isInAnalisys}
                >
                  {t('button.answerKey')}
                </Button>
              )}
            </>
          ) : (
            <>
              {canManageMockExam && (
                <Button
                  variant="outline"
                  fullWidth
                  asChild
                >
                  <Link to={`/simulados/gerenciar/${mockQuestionBook?.id}`}>{t('button.manage')}</Link>
                </Button>
              )}

              {shouldRenderOptions ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <FiChevronDown size={20} />
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end">
                    {mockQuestionBook?.['question-count'] > 0 && mockQuestionBook?.['question-count'] <= 100 && usePermission('Editar simulados', 'Provas e simulados') && (
                      <DropdownMenuItem asChild>
                        <Link to={`/avaliacoes/simulados/offline/${mockQuestionBook?.id}`}>{t('button.offlineExam')}</Link>
                      </DropdownMenuItem>
                    )}
                    {usePermission('Editar simulados', 'Provas e simulados') && (
                      <DropdownMenuItem asChild>
                        <Link to={`/simulados/criar/curso/${mockQuestionBook?.id}`}>{t('button.edit')}</Link>
                      </DropdownMenuItem>
                    )}
                    {usePermission('Excluir simulados', 'Provas e simulados') && <DropdownMenuItem onClick={() => handleDelete(mockQuestionBook)}>{t('button.delete')}</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </>
          )}
        </ButtonGroup>
      </CardFooter>

      <Sheet
        open={showModal}
        onOpenChange={setShowModal}
      >
        <SheetContent size="md">
          <SheetBody className="pb-6 pt-12">
            <ExamsResolutionAnswerKey
              examId={mockQuestionBook.id}
              amountQuestions={mockQuestionBook['question-count']}
              title="Gabarito do simulado"
            />
          </SheetBody>
        </SheetContent>
      </Sheet>
    </Card>
  );
}
